/**
 * Update Document Customer Titles
 * This script updates existing documents with the latest customer title data
 */

import { directSupabase } from '../lib/supabaseClient';

export async function updateDocumentCustomerTitles() {
  console.log('🔄 Starting document customer title update...');
  
  try {
    // Step 1: Get all documents that have customer relationships but missing titles
    console.log('📊 Fetching documents with customer relationships...');
    const { data: documents, error: docError } = await directSupabase
      .from('documents')
      .select('id, customer_id, customer_first_name, customer_last_name, customer_title, title')
      .not('customer_id', 'is', null)
      .is('customer_title', null); // Only documents with null customer_title
    
    if (docError) {
      throw new Error(`Failed to fetch documents: ${docError.message}`);
    }
    
    console.log(`Found ${documents?.length || 0} documents with missing customer titles`);
    
    if (!documents || documents.length === 0) {
      console.log('✅ No documents need customer title updates!');
      return {
        success: true,
        totalDocuments: 0,
        updated: 0,
        results: []
      };
    }
    
    // Step 2: For each document, look up the current customer title
    let updatedCount = 0;
    const results = [];
    
    for (const doc of documents) {
      try {
        // Get customer data from customers table
        const { data: customer, error: customerError } = await directSupabase
          .from('customers')
          .select('id, name, title')
          .eq('id', doc.customer_id)
          .single();
        
        if (customerError) {
          console.log(`⚠️ Customer not found for document "${doc.title}" (customer_id: ${doc.customer_id})`);
          results.push({
            documentId: doc.id,
            documentTitle: doc.title,
            customerId: doc.customer_id,
            status: 'customer_not_found',
            error: customerError.message
          });
          continue;
        }
        
        if (!customer.title) {
          console.log(`⏭️ Customer has no title for document "${doc.title}" (${customer.name})`);
          results.push({
            documentId: doc.id,
            documentTitle: doc.title,
            customerName: customer.name,
            status: 'no_title_to_update'
          });
          continue;
        }
        
        // Update document with customer title
        console.log(`🔄 Updating "${doc.title}" with customer title: "${customer.title}" (${customer.name})`);
        
        const { error: updateError } = await directSupabase
          .from('documents')
          .update({ customer_title: customer.title })
          .eq('id', doc.id);
        
        if (updateError) {
          console.error(`❌ Failed to update document "${doc.title}":`, updateError);
          results.push({
            documentId: doc.id,
            documentTitle: doc.title,
            customerName: customer.name,
            customerTitle: customer.title,
            status: 'failed',
            error: updateError.message
          });
        } else {
          updatedCount++;
          console.log(`✅ Updated "${doc.title}" → ${customer.title} ${customer.name}`);
          results.push({
            documentId: doc.id,
            documentTitle: doc.title,
            customerName: customer.name,
            customerTitle: customer.title,
            status: 'success'
          });
        }
        
      } catch (error) {
        console.error(`❌ Error processing document "${doc.title}":`, error);
        results.push({
          documentId: doc.id,
          documentTitle: doc.title,
          status: 'error',
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    }
    
    // Step 3: Summary
    console.log('✅ Document customer title update completed!');
    console.log(`📊 Summary:`);
    console.log(`   - Total documents processed: ${documents.length}`);
    console.log(`   - Successfully updated: ${updatedCount}`);
    console.log(`   - Customer not found: ${results.filter(r => r.status === 'customer_not_found').length}`);
    console.log(`   - No title to update: ${results.filter(r => r.status === 'no_title_to_update').length}`);
    console.log(`   - Failed: ${results.filter(r => r.status === 'failed').length}`);
    
    // Show successful updates
    const successful = results.filter(r => r.status === 'success');
    if (successful.length > 0) {
      console.log('✅ Successfully updated documents:');
      console.table(successful);
    }
    
    // Show failures if any
    const failed = results.filter(r => r.status === 'failed');
    if (failed.length > 0) {
      console.log('❌ Failed updates:');
      console.table(failed);
    }
    
    return {
      success: true,
      totalDocuments: documents.length,
      updated: updatedCount,
      customerNotFound: results.filter(r => r.status === 'customer_not_found').length,
      noTitleToUpdate: results.filter(r => r.status === 'no_title_to_update').length,
      failed: failed.length,
      results
    };
    
  } catch (error) {
    console.error('❌ Document customer title update failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

// Helper function to run from browser console
(window as any).updateDocumentTitles = updateDocumentCustomerTitles;
















